/**
 * Shared Meta Pixel / Conversions API helpers.
 *
 * The Pixel ID is public (it ships in client JS). The CAPI access token stays
 * server-side as META_CAPI_ACCESS_TOKEN.
 */

export const META_PIXEL_ID = "2655958721451805";

export const META_STANDARD_EVENTS = {
  PAGE_VIEW: "PageView",
  COMPLETE_REGISTRATION: "CompleteRegistration",
  INITIATE_CHECKOUT: "InitiateCheckout",
  PURCHASE: "Purchase"
} as const;

export const META_CUSTOM_EVENTS = {
  ONBOARDING_SELECTION: "onboarding_selection"
} as const;

export const META_EVENT_NAMES = [
  META_STANDARD_EVENTS.PAGE_VIEW,
  META_CUSTOM_EVENTS.ONBOARDING_SELECTION,
  META_STANDARD_EVENTS.COMPLETE_REGISTRATION,
  META_STANDARD_EVENTS.INITIATE_CHECKOUT,
  META_STANDARD_EVENTS.PURCHASE
] as const;

export type MetaEventName = (typeof META_EVENT_NAMES)[number];

export const isMetaEventName = (value: unknown): value is MetaEventName =>
  typeof value === "string" && (META_EVENT_NAMES as readonly string[]).includes(value);

/** Meta requires SHA-256 of trimmed lowercase email. */
export const normalizeEmail = (email: string): string => email.trim().toLowerCase();

// Server-only: node:crypto is loaded lazily so browser bundles importing this
// module (MetaPixelService) don't drag in webpack's crypto polyfill chain.
export const hashSha256 = (value: string): string => {
  const crypto = (globalThis as any).process?.getBuiltinModule?.("node:crypto");
  if (!crypto) {
    throw new Error("hashSha256 is server-only");
  }
  return crypto.createHash("sha256").update(value).digest("hex");
};

/**
 * Reconstruct `_fbc` when the cookie is missing but the landing URL had `fbclid`
 * (common on the first hit, before the Pixel script has written the cookie).
 */
export const buildFbc = (fbclid: string, timestampSeconds = Math.floor(Date.now() / 1000)): string =>
  `fb.1.${timestampSeconds}.${fbclid}`;

export const readCookie = (name: string, cookieHeader = ""): string | undefined => {
  if (!cookieHeader) {
    return undefined;
  }
  const prefix = `${name}=`;
  const match = cookieHeader
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!match) {
    return undefined;
  }
  try {
    return decodeURIComponent(match.slice(prefix.length));
  } catch {
    return match.slice(prefix.length);
  }
};

export type CapiUserInput = {
  email?: string;
  externalId?: string;
  fbp?: string;
  fbc?: string;
  clientIpAddress?: string;
  clientUserAgent?: string;
};

export type CapiCustomData = {
  value?: number;
  currency?: string;
  contentName?: string;
  contentCategory?: string;
  method?: string;
};

/**
 * Meta's Graph API user_data object. Keys are the snake_case names Meta expects.
 */
export const buildCapiUserData = (input: CapiUserInput): Record<string, unknown> => {
  const userData: Record<string, unknown> = {};
  if (input.email) {
    userData["em"] = [hashSha256(normalizeEmail(input.email))];
  }
  if (input.externalId) {
    userData["external_id"] = [hashSha256(input.externalId)];
  }
  if (input.fbp) {
    userData["fbp"] = input.fbp;
  }
  if (input.fbc) {
    userData["fbc"] = input.fbc;
  }
  if (input.clientIpAddress) {
    userData["client_ip_address"] = input.clientIpAddress;
  }
  if (input.clientUserAgent) {
    userData["client_user_agent"] = input.clientUserAgent;
  }
  return userData;
};

export const buildCapiCustomData = (input: CapiCustomData = {}): Record<string, unknown> | undefined => {
  const customData: Record<string, unknown> = {};
  if (typeof input.value === "number" && Number.isFinite(input.value)) {
    customData["value"] = input.value;
  }
  if (input.currency) {
    customData["currency"] = input.currency.toUpperCase();
  }
  if (input.contentName) {
    customData["content_name"] = input.contentName;
  }
  if (input.contentCategory) {
    customData["content_category"] = input.contentCategory;
  }
  if (input.method) {
    customData["method"] = input.method;
  }
  return Object.keys(customData).length > 0 ? customData : undefined;
};
